/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { setKey } from 'react-geocode';
import { AlertColor } from '@mui/material';

import { getStoredCustomerDetails } from '../../../../Shared/Utilities/utils';

import { FacilityDetails, FacilityApiResponse } from '../../Models/AddFacility.Model';
import { AddressLocationState } from '../../../../Shared/Models/Location.Model';
import { CurrentAddress } from '../../../../Shared/Components/Common/GoogleMapLocationSelector/GoogleMapLocationSelector.Model';
import FacilityApiService from '../../Services/FacilityApiService';
import { AddressData, FacilityData, FacilityInput } from '../../../../API';

import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import CurrentLocationCard from './CurrentLocationCard';
import { MapLoader } from '../../../../Shared/Components/Common/MapLoader/MapLoader';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import GoogleMapLocationSelector from '../../../../Shared/Components/Common/GoogleMapLocationSelector/GoogleMapLocationSelector';
import { LocationCardContainer } from '../../../Vendor/Components/AllJobs/ModifyGeofence/ModifyGeoFence.Style';

const AddFacilityPage = (): JSX.Element => {
  const location = useLocation();
  const state = location.state as AddressLocationState;

  const [currentLocation, setCurrentLocation] = useState({ lat: 0, lng: 0 });
  const [loading, setLoading] = useState(false);
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);
  const [currentAddress, setCurrentAddress] = useState<CurrentAddress | undefined>(undefined);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [addressExists, setAddressExists] = useState(state?.addressExists || false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  const navigate = useNavigate();
  const { handleSubmit, control, getValues, setValue } = useForm({
    defaultValues: {
      facilityName: '',
      suitNumber: '',
      facilityType: '',
    },
  });

  const { t } = useTranslation(['addFacility', 'jobManagement']);

  const { customerId = '' } = getStoredCustomerDetails() as { customerId: string };
  const addressId = state?.address?.addressId ?? '';
  const facilityAddress = state?.address?.addressLine2 || state?.address?.addressLine1;

  const onSubmit = async (facilityDetails: FacilityDetails): Promise<void> => {
    try {
      if (location.state && currentAddress && !location?.state?.isFromJob) {
        const facilityPayload = {
          facilityId: state.facilityId,
          customerId: state.customerId,
          buildingName: facilityDetails.facilityName,
          noOfFloors: 0,
          sqFootage: 0,
          contactId: '',
          mdFacilityType: facilityDetails.facilityType,
        } as FacilityData;
        const addressPayload = {
          addressId: addressId,
          customerId: customerId,
          noOfBuildings: 1,
          addressName: '',
          addressLine1: currentAddress.addressLine1,
          addressLine2: currentAddress.addressLine2,
          city: currentAddress.city,
          stateCode: currentAddress.stateCode,
          county: currentAddress.country,
          postalCode: currentAddress.postalCode,
          mdCountryCode: 'US',
          landmark: '',
          googlePlaceId: currentAddress.googlePlaceId,
          latitude: currentLocation.lat,
          longitude: currentLocation.lng,
        } as AddressData;

        const updateFacilityRes: FacilityApiResponse = await FacilityApiService.updateFacility(facilityPayload);
        const updateAddressRes: FacilityApiResponse = await FacilityApiService.updateAddress(addressPayload);

        const facilityErrors = updateFacilityRes?.errors ?? [];
        const addressErrors = updateAddressRes?.errors ?? [];

        if (facilityErrors.length === 0 && addressErrors.length === 0) {
          setIsSubmissionSuccess(true);
        } else {
          console.error(t('addFacility:errorWhileUpdatingFacility'), facilityErrors, addressErrors);
        }
      } else if (currentAddress) {
        const payload: FacilityInput = {
          customerId: location?.state?.customerId ?? customerId,
          buildingName: facilityDetails.facilityName,
          noOfFloors: 0,
          sqFootage: 0,
          contactId: '',
          mdFacilityType: facilityDetails.facilityType,
          address: {
            customerId: location?.state?.customerId ?? customerId,
            noOfBuildings: 1,
            addressName: '',
            addressLine1: currentAddress?.addressLine1,
            addressLine2: currentAddress?.addressLine2,
            city: currentAddress?.city,
            stateCode: currentAddress?.stateCode,
            county: currentAddress?.country,
            postalCode: currentAddress?.postalCode,
            mdCountryCode: 'US',
            landmark: '',
            googlePlaceId: currentAddress?.googlePlaceId,
            latitude: currentLocation.lat,
            longitude: currentLocation.lng,
          },
        } as FacilityInput;

        const facilityResponse: FacilityApiResponse = await FacilityApiService.addFacility(payload);

        if (facilityResponse.errors.length === 0) {
          setIsSubmissionSuccess(true);
        } else {
          console.error(t('addFacility:errorWhileAddingFacility'), facilityResponse.errors);
        }
      }
    } catch (e) {
      console.error(t('addFacility:EditingFacility'), e);
    }
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  useEffect(() => {
    setLoading(true);
    setKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLoading(false);
        },
        (error) => {
          console.error(t('googleMap:errorWhileFetchingCurrentLocation'), error);
          setLoading(false);
        }
      );
    } else {
      console.error(t('googleMap:geolocationNotSupportedByBrowser'));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSubmissionSuccess && location?.state?.isFromJob) {
      navigate(-1);
    }
  }, [isSubmissionSuccess]);

  return (
    <>
      <PageTitle title={t('addFacility:facilityAddress')} />
      {loading ? (
        <MapLoader />
      ) : (
        <GoogleMapLocationSelector
          currentLocation={currentLocation}
          setCurrentLocation={setCurrentLocation}
          setCurrentAddress={setCurrentAddress}
          zoomValue={4}
          showSnackbar={showSnackbar}
          addressExists={addressExists}
          setAddressExists={setAddressExists}
          showSearchInput={showSearchInput}
        />
      )}

      <LocationCardContainer>
        <CurrentLocationCard
          control={control}
          setValue={setValue}
          onSubmit={handleSubmit(onSubmit)}
          onClose={() => setIsSubmissionSuccess(false)}
          isSuccess={isSubmissionSuccess}
          currentAddress={addressExists ? facilityAddress : (currentAddress && currentAddress.formattedAddress) || ''}
          loading={loading}
          getValues={getValues}
          SearchField={() => setShowSearchInput(!showSearchInput)}
        />
      </LocationCardContainer>

      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default AddFacilityPage;
